import Grid from "@mui/material/Grid";
import {Driver} from "../../../data/models/bracket_api/Driver";
import Typography from "@mui/material/Typography";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    useMediaQuery,
    useTheme,
    Alert,
    Snackbar,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {Fragment, useCallback, useState} from "react";
import {Bracket} from "../../../data/models/bracket_api/Bracket";
import {Event} from "../../../data/models/bracket_api/Event";
import {Qualifier} from "../../../data/models/bracket_api/Qualifier";
import {RaceClass} from "../../../data/models/bracket_api/RaceClass";
import * as React from "react";
import Button from "@mui/material/Button";
import {fetchQualifyingLadderByEventName, QualifyingLadderEntry} from "../../../apis/LadderGateway";
import {isGatewayConfigured} from "../../../gatewayConfig";

type QualifierRowProps = {
    qualifier: Qualifier,
    drivers: Driver[],
    editQualifier: (division: RaceClass, seed: number, elapsed_time?: number, speed?: number, driver?: Driver) => void,
    division: string,
    qualifiers: Qualifier[],
}

function QualifierRow(props: Readonly<QualifierRowProps>) {
    const [driver, setDriver] = useState<Driver | undefined>(props.qualifier.driver);

    React.useEffect(() => {
        setDriver(props.qualifier.driver);
    }, [props.qualifier.driver]);

    function onChange(driver?: Driver, elapsedTime?: number,  speed?: number) {
        props.editQualifier(
            props.qualifier.race_class,
            props.qualifier.seed,
            elapsedTime,
            speed,
            driver
        );
    }

    return (
        <Grid container sx={{
            display: 'flex',
            flexDirection: 'row',
            minWidth: '100%',
            flexWrap: 'nowrap',
            alignItems: 'center',
        }}>
            <Typography variant={'h6'} align={'left'} sx={{
                marginRight: '12px',
            }}>
                {props.qualifier.seed}
            </Typography>
            <FormControl sx={{
                marginY: "12px",
                marginX: '12px',
            }}>
                <InputLabel id={"driver-label" + props.division + props.qualifier.seed}>Driver</InputLabel>
                <Select
                    labelId={"driver-label" + props.division + props.qualifier.seed}
                    label={"Driver"}
                    name={'driver'}
                    defaultValue={props.qualifier.driver?.id}
                    sx={{
                        minWidth: '200px',
                    }}
                    value={driver?.id}
                    onChange={(event) => {
                        let driver = props.drivers.find(driver => driver.id === event.target.value);
                        setDriver(driver)
                        onChange(driver);
                    }}
                >
                    {
                        props.drivers.filter((driver2) => {return props.qualifiers.find((qualifier) => {
                            return qualifier.driver === driver2 && qualifier.seed !== props.qualifier.seed
                        }) === undefined}).map((driver: Driver) => {
                            return <MenuItem key={driver.id}
                                             value={driver.id}>{driver.first_name + " " + driver.last_name}</MenuItem>
                        })
                    }
                </Select>
            </FormControl>
            <TextField
                type={'text'}
                label="Qualifying Time"
                variant="filled"
                name={'elapsed-time'}
                value={props.qualifier.elapsed_time ?? ''}
                sx={{
                    width: 'fit-content',
                    minWidth: '125px',
                    marginY: 1,
                    marginRight: '12px',
                }}
                onChange={(event) => {
                    if (event.target.value === '') {
                        onChange(driver, 0, props.qualifier.speed);
                    } else {
                        try {
                            let time = parseFloat(event.target.value);
                            onChange(driver, time, props.qualifier.speed);
                        }
                        catch (e) {
                            console.error(e)
                        }
                    }
                }}
            />
            <TextField
                type={'text'}
                label="Speed (MPH)"
                variant="filled"
                name={'speed'}
                value={props.qualifier.speed ?? ''}
                sx={{
                    width: 'fit-content',
                    minWidth: '125px',
                    marginY: 1,
                    marginRight: '12px',
                }}
                onChange={(event) => {
                    if (event.target.value === '') {
                        onChange(driver, props.qualifier.elapsed_time, 0);
                    }
                    else {
                        try {
                            let speed = parseFloat(event.target.value);
                            onChange(driver, props.qualifier.elapsed_time, speed);
                        }
                        catch (e) {
                            console.error(e)
                        }
                    }
                }}
            />
        </Grid>
    )
}

type QualifierFormProps = {
    drivers: Driver[],
    division: string,
    bracket: Bracket,
    event: Event,
    qualifiers: Qualifier[],
    editQualifier: (division: RaceClass, seed: number, elapsed_time?: number, speed?: number, driver?: Driver) => void,
    adjustNumberOfQualifiers: (division: RaceClass, numberOfQualifiers: number) => void,
    setQualifiersForDivision: (division: RaceClass, qualifiers: Qualifier[]) => void,
    eventName: string,
    raceClassInt: number,
}
export default function QualifierForm(props: Readonly<QualifierFormProps>) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [isPulling, setIsPulling] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info");

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const findDriverForLadderEntry = useCallback((entry: QualifyingLadderEntry): Driver | undefined => {
        // Match driver by DRBB id -> Driver.id
        return props.drivers.find((driver) => driver.id === entry.drbb_id);
    }, [props.drivers]);

    const handlePullQualifiers = useCallback(async () => {
        if (!isGatewayConfigured()) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Qualifying ladder service is not configured.");
            setSnackbarOpen(true);
            return;
        }

        try {
            setIsPulling(true);
            const response = await fetchQualifyingLadderByEventName(props.eventName, props.raceClassInt);
            const ladder = response.data.ladder;
            const count = response.data.count ?? ladder.length;

            if (!ladder || ladder.length === 0) {
                setSnackbarSeverity("info");
                setSnackbarMessage("No qualifiers returned for this event and category.");
                setSnackbarOpen(true);
                return;
            }

            const division = props.division as RaceClass;
            const newQualifiers: Qualifier[] = ladder.map((entry) => ({
                seed: entry.position,
                race_class: division,
                bracket: props.bracket,
                event: props.event,
                driver: findDriverForLadderEntry(entry),
                elapsed_time: entry.best_et,
                speed: entry.best_mph,
            }));

            // If count is greater than ladder length, add empty slots; if smaller, trim
            let position = newQualifiers.length + 1;
            while (newQualifiers.length < count) {
                newQualifiers.push({
                    seed: position,
                    race_class: division,
                    bracket: props.bracket,
                    event: props.event,
                });
                position++;
            }
            if (newQualifiers.length > count) {
                newQualifiers.splice(count);
            }

            props.setQualifiersForDivision(division, newQualifiers);

            setSnackbarSeverity("success");
            setSnackbarMessage("Qualifiers pulled from ladder service.");
            setSnackbarOpen(true);
        } catch (error) {
            console.error(error);
            setSnackbarSeverity("error");
            setSnackbarMessage("Failed to pull qualifiers from ladder service.");
            setSnackbarOpen(true);
        } finally {
            setIsPulling(false);
        }
    }, [props.division, props.eventName, props.raceClassInt, props.bracket, props.event, props.setQualifiersForDivision, findDriverForLadderEntry]);

    return (
        <Fragment>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{width: '100%'}}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Button variant="contained" onClick={handleClickOpen} sx={{
                backgroundColor: "#486ce7",
                color: "#ffffff",
                borderRadius: 0,
                ":hover": {
                    backgroundColor: "#c40000",
                    color: "#ffffff",
                },
                width: '100%',
            }}>
                <Typography variant={'h6'}>
                    Edit {props.division} Qualifiers
                </Typography>
            </Button>
            <Dialog
                fullScreen={fullScreen}
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    margin: 'auto',
                    minWidth: '900px'
                }}
            >
                <DialogTitle id="responsive-dialog-title">
                    {props.division} Qualifiers
                </DialogTitle>
                <DialogContent>
                    <Button
                        variant="contained"
                        onClick={handlePullQualifiers}
                        disabled={props.qualifiers.length > 0 || isPulling}
                        sx={{
                            backgroundColor: "#486ce7",
                            color: "#ffffff",
                            borderRadius: 0,
                            ":hover": {
                                backgroundColor: "#c40000",
                                color: "#ffffff",
                            },
                            width: '100%',
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant={'h6'}>
                            {isPulling ? "Pulling Qualifiers..." : "Pull Qualifiers"}
                        </Typography>
                    </Button>
                    <TextField
                        type={'text'}
                        onWheel={event => { event.preventDefault(); }}
                        label="Number of Qualifiers"
                        variant="filled"
                        value={props.qualifiers.length || ''}
                        name={'number-of-qualifiers'}
                        sx={{
                            width: '100%',
                            marginY: 1,
                            marginRight: '12px',
                        }}
                        onChange={(event) => {
                            if (event.target.value === '') {
                                props.adjustNumberOfQualifiers(props.division as RaceClass, 0);
                                return;
                            }
                            props.adjustNumberOfQualifiers(props.division as RaceClass, parseInt(event.target.value));
                        }}
                    />

                    {
                        props.qualifiers.map((qualifier: Qualifier) => {
                            return <QualifierRow key={qualifier.race_class + qualifier.seed}
                                                 qualifier={qualifier}
                                                 qualifiers={props.qualifiers}
                                                 drivers={props.drivers}
                                                 editQualifier={props.editQualifier} division={props.division}/>
                        })
                    }
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    )
}